import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Users, TrendingUp, AlertTriangle, Shield } from 'lucide-react';
import { useSeller } from '@/components/layout/DashboardLayout';

function getRiskBadge(score) {
  // Health score: 100=healthy, 0=critical
  if (score >= 75) return { label: 'Low Risk', className: 'bg-[hsl(142,72%,45%)]/15 text-[hsl(142,72%,45%)] border-[hsl(142,72%,45%)]/30' };
  if (score >= 50) return { label: 'Moderate', className: 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30' };
  if (score >= 25) return { label: 'High Risk', className: 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30' };
  return { label: 'Critical', className: 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30' };
}

export default function AdminPage() {
  const { setCurrentSellerId } = useSeller();
  const [sellers, setSellers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sellersRes, statsRes] = await Promise.all([
          adminAPI.getSellers(),
          adminAPI.getStats(),
        ]);
        setSellers(sellersRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error('Failed to load admin data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-xl font-semibold">Accounts</h1>
        <p className="text-sm text-muted-foreground">Manage all seller accounts and view aggregate risk</p>
      </div>

      {/* Admin Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-card border-border/80">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="w-10 h-10 rounded-lg bg-[hsl(188,100%,55%)]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[hsl(188,100%,55%)]" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">{stats.total_accounts}</p>
                <p className="text-xs text-muted-foreground">Total Accounts</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/80">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="w-10 h-10 rounded-lg bg-[hsl(264,92%,70%)]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[hsl(264,92%,70%)]" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">{stats.avg_risk_score}</p>
                <p className="text-xs text-muted-foreground">Avg Risk Score</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/80">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="w-10 h-10 rounded-lg bg-[hsl(0,84%,60%)]/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[hsl(0,84%,60%)]" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">{stats.critical_zone}</p>
                <p className="text-xs text-muted-foreground">Critical Zone</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/80">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="w-10 h-10 rounded-lg bg-[hsl(24,94%,56%)]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[hsl(24,94%,56%)]" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">{stats.high_risk}</p>
                <p className="text-xs text-muted-foreground">High Risk</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sellers Table */}
      <Card className="bg-card border-border/80" data-testid="admin-accounts-table">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">All Seller Accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="text-xs font-semibold">Account Name</TableHead>
                  <TableHead className="text-xs font-semibold">Users</TableHead>
                  <TableHead className="text-xs font-semibold">Risk Score</TableHead>
                  <TableHead className="text-xs font-semibold">Breached Metrics</TableHead>
                  <TableHead className="text-xs font-semibold">Last Updated</TableHead>
                  <TableHead className="text-xs font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellers.map((seller) => {
                  const risk = getRiskBadge(seller.risk_score);
                  return (
                    <TableRow key={seller.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => setCurrentSellerId(seller.id)}>
                      <TableCell className="text-sm font-medium">{seller.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {seller.users?.map(u => u.name).join(', ') || 'No users'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold tabular-nums">{seller.risk_score}</span>
                          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 border', risk.className)}>
                            {risk.label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{seller.breached_metrics} / {seller.total_metrics}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(seller.last_updated).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <span className="text-xs text-[hsl(188,100%,55%)] cursor-pointer hover:underline">View Dashboard</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
